
import { TypedVue } from "@/store/types";
import { Component, Inject, Prop } from "vue-property-decorator";
import VueI18n from "vue-i18n";
import { CompetencyStat, LeaderSummary, PCT_BAND_COLORS, pctFor, PRIORITY_ORDER, ReportFilters, Subgroup } from "./shared";

@Component
export default class TeamPctBars extends TypedVue {
  @Inject("$i18n") readonly $i18n!: VueI18n;

  @Prop({ required: true }) readonly stats!: CompetencyStat[];
  @Prop({ required: true }) readonly visibleLeaders!: LeaderSummary[];
  @Prop({ required: true }) readonly subgroups!: Subgroup[];
  @Prop({ required: true }) readonly filters!: ReportFilters;

  get bands() {
    return (["high", "good", "mixed", "low"] as const).map((key) => ({ key, color: PCT_BAND_COLORS[key] }));
  }

  // Subgroup chosen in "Order by" (null = whole team). Falls back to the team when that
  // subgroup is no longer shown.
  public orderById: string | null = null;

  get orderSubgroup(): Subgroup | null {
    return this.subgroupRows.find((sg) => sg.id === this.orderById) ?? null;
  }

  // Ranked most → least prioritized, by the chosen subgroup when one is picked (ties fall
  // back to the team percentage).
  get ranked(): CompetencyStat[] {
    const sg = this.orderSubgroup;
    const sgPct = sg ? new Map(this.stats.map((s) => [s.trait, this.subgroupPct(s.trait, sg)])) : null;
    return [...this.stats].sort((a, b) => (sgPct ? (sgPct.get(b.trait) ?? 0) - (sgPct.get(a.trait) ?? 0) : 0) || b.pct - a.pct || a.name.localeCompare(b.name));
  }

  public groupByPriority = false;

  get displayGroups(): Array<{ key: string; stats: CompetencyStat[] }> {
    if (!this.groupByPriority) return [{ key: "all", stats: this.ranked }];
    return PRIORITY_ORDER.map((key) => ({ key, stats: this.ranked.filter((s) => s.priorityKey === key) })).filter((g) => g.stats.length > 0);
  }

  // Only subgroups that are active and have at least one visible member get a breakdown bar.
  get subgroupRows(): Subgroup[] {
    if (!this.filters.showSubgroups) return [];
    return this.subgroups.filter((sg) => this.filters.activeSubgroupIds.includes(sg.id) && this.membersOf(sg).length > 0);
  }

  // The team row is replaced by the subgroup breakdown whenever subgroups are shown.
  get showTeamRow(): boolean {
    return this.subgroupRows.length === 0;
  }

  membersOf(sg: Subgroup): LeaderSummary[] {
    return this.visibleLeaders.filter((l) => sg.memberIds.includes(l.id));
  }

  subgroupPct(trait: string, sg: Subgroup): number {
    return pctFor(trait, this.membersOf(sg));
  }
}
